import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Bell, LogOut, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  const [timesheetBadgeCount, setTimesheetBadgeCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchAlertCount();
      fetchTimesheetBadge();
    }
  }, [user, location.pathname]);

  const fetchAlertCount = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts`, {
        withCredentials: true
      });
      const count = (data.zero_activity?.length || 0) + (data.overdue_follow_ups?.length || 0);
      setAlertCount(count);
    } catch (error) {
      console.error('Failed to fetch alert count:', error);
    }
  };

  const fetchTimesheetBadge = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/timesheet/unsubmitted-count`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Only show badge for Super Admin
      if (user.role === 'poc') {
        // POC sees their own unsubmitted count
        setTimesheetBadgeCount(data.count || 0);
      } else if (user.role === 'admin' || user.role === 'super_admin') {
        // Admin and Super Admin see total of all POCs' unsubmitted entries
        const total = (data.unsubmitted_by_poc || []).reduce((sum, poc) => sum + poc.count, 0);
        setTimesheetBadgeCount(total);
      }
    } catch (error) {
      console.error('Failed to fetch timesheet badge count:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-900 border-purple-900';
      case 'admin':
        return 'bg-blue-100 text-blue-900 border-blue-900';
      case 'poc':
        return 'bg-[#D1FAE5] text-[#065F46] border-[#065F46]';
      default:
        return 'bg-[#F4F4F5] text-[#09090B] border-[#09090B]';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'SUPER ADMIN';
      case 'admin':
        return 'ADMIN';
      case 'poc':
        return 'POC';
      default:
        return role.toUpperCase();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] h-16 px-6 flex items-center justify-between sticky top-0 z-50">
        {/* Logo Area */}
        <div className="flex items-center gap-3">
          {/* Square Icon with ECG */}
          <div 
            className="w-8 h-8 flex items-center justify-center overflow-hidden bg-[#0C9168]"
            style={{ borderRadius: '12px' }}
          >
            {/* ECG Line Icon */}
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="text-white"
            >
              <path 
                d="M3 12 L7 12 L9 8 L11 16 L13 10 L15 14 L17 12 L21 12" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          
          {/* Text */}
          <div className="flex flex-col">
            <div className="text-lg font-bold font-headings leading-none text-[#09090B]">
              Vitals
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#52525B] font-body">
              ASSUREAI
            </div>
          </div>
        </div>

        {/* Center Nav */}
        <nav className="flex items-center gap-8">
          <Link
            to="/dashboard"
            data-testid="nav-dashboard"
            className={`text-sm font-medium transition-colors ${
              location.pathname === '/dashboard'
                ? 'text-[#0C9168] border-b-2 border-[#0C9168] pb-1'
                : 'text-[#52525B] hover:text-[#09090B]'
            }`}
          >
            Dashboard
          </Link>
          <Link
            to="/alerts"
            data-testid="nav-alerts"
            className={`text-sm font-medium transition-colors relative ${
              location.pathname === '/alerts'
                ? 'text-[#0C9168] border-b-2 border-[#0C9168] pb-1'
                : 'text-[#52525B] hover:text-[#09090B]'
            }`}
          >
            <Bell className="inline w-4 h-4 mr-1" strokeWidth={1.5} />
            Alerts
            {alertCount > 0 && (
              <span className="absolute -top-2 -right-6 bg-[#DC2626] text-white text-xs px-1.5 py-0.5 font-mono">
                {alertCount}
              </span>
            )}
          </Link>
          
          {/* Timesheet Link - Accessible to all roles */}
          <Link
            to="/timesheet"
            data-testid="nav-timesheet"
            className={`text-sm font-medium transition-colors relative ${
              location.pathname === '/timesheet'
                ? 'text-[#0C9168] border-b-2 border-[#0C9168] pb-1'
                : 'text-[#52525B] hover:text-[#09090B]'
            }`}
          >
            <Clock className="inline w-4 h-4 mr-1" strokeWidth={1.5} />
            Timesheet
          </Link>
          
          {/* My Activity Link - Only for Admins */}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Link
              to="/my-activity"
              data-testid="nav-my-activity"
              className={`text-sm font-medium transition-colors relative ${
                location.pathname === '/my-activity'
                  ? 'text-[#0C9168] border-b-2 border-[#0C9168] pb-1'
                  : 'text-[#52525B] hover:text-[#09090B]'
              }`}
            >
              <Clock className="inline w-4 h-4 mr-1" strokeWidth={1.5} />
              My Activity
            </Link>
          )}
        </nav>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {user?.role === 'super_admin' && (
            <Link
              to="/settings"
              data-testid="nav-settings"
              className="text-[#52525B] hover:text-[#09090B] transition-colors"
            >
              <Settings className="w-5 h-5" strokeWidth={1.5} />
            </Link>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#09090B] font-medium">{user?.name}</span>
            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase border ${getRoleBadgeColor(user?.role)}`}>
              {getRoleLabel(user?.role)}
            </span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="text-[#52525B] hover:text-[#09090B] transition-colors"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 md:p-8">{children}</main>
    </div>
  );
}